import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { pickBy } from '../../../util/common';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  IconArrowHead,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import BagIcon from './BagIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

import css from './Topbar.module.css';
import { getCurrentUserTypeRoles, showCreateListingLinkForUser } from '../../../util/userHelpers';

const MAX_MOBILE_SCREEN_WIDTH = 1024; // Burger/mobile through 1024px; desktop from 1025px.

const MOBILE_MENU_BUTTON_ID = 'mobileMenuButton';
const DESKTOP_MENU_BUTTON_ID = 'desktopMenuButton';
const MOBILE_SEARCH_BUTTON_ID = 'mobileSearchButton';
const MENU_CONTENT_ID = 'TopbarMobileMenu';

// Pages that should use the "search" variant of the topbar.
// To add a new page, just add its route name here.
const SEARCH_VARIANT_PAGES = [
  'SearchPage',
  'SearchPageWithListingType',
  'CMSPage:journal',
  "CMSPage:our-story",
  "CMSPage:how-it-work",
  "CMSPage:exchanges-and-shipping",
  "CMSPage:fee-seclude",
  "CMSPage:concierge",
  "CMSPage:journal",
  "CMSPage:sustainability",
  "CMSPage:become-an-ambassador",
  "CMSPage:help-center",
  "CMSPage:faq",
  "CMSPage:contact",
  "CMSPage:community",
  "CMSPage:terms-of-service",
  "CMSPage:privacy-policy",
  "CMSPage:protection-policy",
  "CMSPage:shipping-courier-and-carrier-claims-policy",
  "CMSPage:cancellation-refund-policy",
];

// Optional: every page whose name starts with one of these prefixes also matches.
// e.g. add 'CMSPage:' to match all CMS pages, or 'InboxPage:' to match all inbox tabs.
const SEARCH_VARIANT_PREFIXES = [
  // 'CMSPage:',
  // 'InboxPage:',
];

const usesSearchVariant = page =>
  SEARCH_VARIANT_PAGES.includes(page) ||
  SEARCH_VARIANT_PREFIXES.some(prefix => page?.startsWith(prefix));

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  // Modal flag last so it wins over any existing value of the same key.
  const searchString = `?${stringify({ ...parse(search), [modalStateParam]: 'open' })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
  } = props;

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location || {};
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the inbox link:
   * - if only provider role – sales
   * - if only customer role – orders
   * - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
      ? 'orders'
      : currentUserHasListings
        ? 'sales'
        : 'orders';

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      inboxTab={topbarInboxTab}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const isLandingPage =
    resolvedCurrentPage === 'LandingPage' || resolvedCurrentPage === 'CMSPage:landing-page';
  // True for SearchPage and any other page listed in SEARCH_VARIANT_PAGES / SEARCH_VARIANT_PREFIXES
  const isSearchPage = usesSearchVariant(resolvedCurrentPage);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavHidden, setIsNavHidden] = useState(false);
  const [isPromoHidden, setIsPromoHidden] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const MENU_CLOSE_MS = 300;
  const SCROLL_DIRECTION_MIN_STEP = 5;
  const NAV_HIDE_AFTER_Y = 0;

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMenuClosing(false);
      setIsMenuVisible(true);
      return;
    }
    if (!isMenuVisible) {
      return;
    }
    setIsMenuClosing(true);
    const timeoutId = window.setTimeout(() => {
      setIsMenuVisible(false);
      setIsMenuClosing(false);
    }, MENU_CLOSE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isMobileMenuOpen, isMenuVisible]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let prevY = window.scrollY;
    let isFirstTick = true;

    const onScroll = () => {
      const y = window.scrollY;
      // Promo only at the very top — hide it on the way down and keep it
      // hidden on scroll-up until y is 0 again.
      setIsPromoHidden(y > 0);

      // Keep the bar visible at the top of the page and while the menu is open.
      // At the hero, the landing bar stays transparent.
      if (isMobileMenuOpen || y <= NAV_HIDE_AFTER_Y) {
        prevY = y;
        isFirstTick = false;
        setIsNavHidden(false);
        setIsScrolled(false);
        return;
      }

      const delta = y - prevY;
      prevY = y;

      // No clear direction yet: don't paint a black fill on the first downward
      // pixels (that flash is the glitch). If we loaded already past the top,
      // keep type readable until the user scrolls.
      if (Math.abs(delta) < SCROLL_DIRECTION_MIN_STEP) {
        if (isFirstTick && isLandingPage) {
          setIsScrolled(true);
        }
        isFirstTick = false;
        return;
      }

      isFirstTick = false;
      const scrollingDown = delta > 0;
      setIsNavHidden(scrollingDown);

      // Solid bar only when the nav is revealed on scroll-up — not while it
      // is hiding on scroll-down.
      if (isLandingPage) {
        setIsScrolled(!scrollingDown);
      } else {
        setIsScrolled(false);
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isLandingPage, isMobileMenuOpen]);

  const classes = classNames(rootClassName || css.root, className, {
    [css.landing]: isLandingPage,
    [css.search]: isSearchPage,
    [css.scrolled]: isLandingPage && isScrolled,
    [css.promoHidden]: isPromoHidden,
  });

  const mobileBagButton = (
    <NamedLink
      className={css.bagMenu}
      name={isAuthenticated ? 'InboxPage' : 'LoginPage'}
      {...(isAuthenticated ? { params: { tab: topbarInboxTab } } : {})}
      title={intl.formatMessage({ id: 'TopbarDesktop.bag' })}
    >
      <BagIcon
        className={css.bagMenuIcon}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.bag' })}
      />
      {notificationDot}
    </NamedLink>
  );

  const handleSkipToMainContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the main content for screen readers
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      // Remove tabindex after blur to avoid tabbing into it later
      mainContent.addEventListener(
        'blur',
        () => {
          mainContent.removeAttribute('tabindex');
        },
        { once: true }
      );
    }
  };

  return (
    <div
      className={classes}
      data-nav-hidden={isNavHidden ? 'true' : undefined}
      data-promo-hidden={isPromoHidden ? 'true' : undefined}
    >
      <Button onClick={handleSkipToMainContent} className={css.skipToMainContent}>
        <FormattedMessage id="Topbar.skipToMainContent" />
        <IconArrowHead direction="right" size="small" rootClassName={css.skiptoMainArrow} />
      </Button>
      <div className={css.header}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={handleLogout}
          currentPage={resolvedCurrentPage}
        />
        <div className={css.promo} aria-hidden={isPromoHidden ? 'true' : undefined}>
          <p className={css.promoText}>
            <FormattedMessage
              id="Topbar.promo"
              values={{
                discount: (
                  <strong>
                    <FormattedMessage id="Topbar.promoDiscount" />
                  </strong>
                ),
              }}
            />
          </p>
        </div>
        <nav className={classNames(mobileRootClassName || css.container, mobileClassName)}>
          <Button
            id={MOBILE_MENU_BUTTON_ID}
            rootClassName={css.menu}
            onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
            title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          >
            <MenuIcon
              className={css.menuIcon}
              ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            />
            {notificationDot}
          </Button>
          <LinkedLogo
            id="logo-topbar-mobile"
            className={css.mobileLogo}
            layout={'mobile'}
            alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
            linkToExternalSite={config?.topbar?.logoLink}
          />
          {mobileBagButton}
        </nav>
        <div className={css.desktop}>
          <TopbarDesktop
            className={desktopClassName}
            variant={isSearchPage ? 'search' : 'default'}
            currentUserHasListings={currentUserHasListings}
            currentUser={currentUser}
            currentPage={resolvedCurrentPage}
            intl={intl}
            isAuthenticated={isAuthenticated}
            notificationCount={notificationCount}
            onLogout={handleLogout}
            isMenuOpen={isMobileMenuOpen}
            onMenuClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
            menuButtonId={DESKTOP_MENU_BUTTON_ID}
            menuId={MENU_CONTENT_ID}
            config={config}
            customLinks={customLinks}
            showCreateListingsLink={showCreateListingsLink}
            inboxTab={topbarInboxTab}
          />
        </div>
      </div>
      <Modal
        id={MENU_CONTENT_ID}
        className={classNames(css.menuModal, { [css.menuClosing]: isMenuClosing })}
        scrollLayerClassName={css.menuScrollLayer}
        containerClassName={css.menuContainer}
        contentClassName={css.menuContent}
        closeNavClassName={css.menuCloseNav}
        closeClassName={css.menuClose}
        closeOnOverlayClick
        isOpen={isMenuVisible}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={isMobileLayout ? MOBILE_MENU_BUTTON_ID : DESKTOP_MENU_BUTTON_ID}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_SEARCH_BUTTON_ID}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
